import { readConsent } from "@/lib/consent";
import { gaEvent } from "@/lib/ga4";
import { analyticsEventSchema } from "@/lib/analytics-schema";
import { formatEventError, formatEventLog } from "@/lib/analytics-log";
import { deriveSectionPosition } from "@/lib/section-visibility";
import {
  CTA_TYPES,
  EVENT_NAMES,
  FORM_STATUSES,
  UTM_KEYS,
  type CtaType,
  type EventName,
  type FormStatus,
  type Utm,
  type UtmKey,
} from "@/lib/analytics-taxonomy";

/**
 * Standardised conversion tracking for CTA clicks and form interactions.
 *
 * Strictly consent-gated: nothing is sent unless the visitor explicitly opted
 * into the "analytics" category (see src/lib/consent.ts). No PII is collected —
 * only the event name, a stable label, the page path, structured context
 * (CTA type / form status) and the campaign (UTM) parameters of the session.
 *
 * Every payload is validated against the shared zod schema
 * (src/lib/analytics-schema.ts) before it leaves the browser, so field names
 * and types stay consistent with the server contract.
 *
 * The full taxonomy is documented in docs/analytics-events.md — keep both in
 * sync and never invent ad-hoc event names at the call site.
 */

export { CTA_TYPES, EVENT_NAMES, FORM_STATUSES, UTM_KEYS };
export type { CtaType, EventName, FormStatus, Utm, UtmKey };

export type AnalyticsEvent = {
  /** Event name from the fixed taxonomy. */
  name: EventName;
  /** Stable human-readable label of the element, e.g. "Chat per WhatsApp?". */
  label: string;
  /** Pathname the event happened on. */
  page: string;
  /** Click target (href or route) for CTA events. */
  target?: string;
  /** CTA classification — required for cta_click. */
  ctaType?: CtaType;
  /** Form identifier, e.g. "technology-consulting-enquiry". */
  formId?: string;
  /** Form lifecycle status — required for form_* events. */
  formStatus?: FormStatus;
  /** Optional non-personal context, e.g. the enquiry topic or error reason. */
  detail?: string;
  /** Optional viewport Y coordinate of the click, for analysing widget interaction. */
  clickY?: number;
  /** Effective scroll offset (px) applied after a jump, so the shown section is derivable. */
  scrollY?: number;
  /** Version tag of the target section for deep_link_focus events. */
  sectionVersion?: string;
  /** Campaign attribution captured for the session. */
  utm?: Utm;
};


const ENDPOINT = "/api/public/events";
const UTM_STORAGE_KEY = "oxory.utm.v1";

declare global {
  interface Window {
    /** Buffer of tracked events, handy for debugging in the console. */
    __events?: AnalyticsEvent[];
    /** Set to true in the console to mirror the server log lines locally. */
    __eventsDebug?: boolean;
  }
}

/** True only when the visitor opted into analytics. */
export function analyticsAllowed(): boolean {
  return readConsent()?.analytics === true;
}

function clip(value: string, max: number) {
  return value.trim().slice(0, max);
}

/**
 * Reads UTM parameters from the current URL and remembers them for the session,
 * so a conversion later in the visit is still attributed to the campaign.
 * First touch wins — a later navigation without UTMs does not overwrite them.
 */
export function captureUtm(
  search: string = typeof window === "undefined" ? "" : window.location.search,
): Utm {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(search);
  const fresh: Utm = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) fresh[key] = clip(value, 120);
  }

  try {
    if (Object.keys(fresh).length > 0) {
      window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fresh));
      return fresh;
    }
    const stored = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored) as Utm;
    const clean: Utm = {};
    for (const key of UTM_KEYS) {
      const value = parsed?.[key];
      if (typeof value === "string" && value) clean[key] = clip(value, 120);
    }
    return clean;
  } catch {
    return fresh;
  }
}

/**
 * Sends a conversion event — a no-op without analytics consent, on the server,
 * or when the beacon fails. Never throws and never blocks the UI.
 */
export function trackEvent(
  name: EventName,
  label: string,
  context: Omit<AnalyticsEvent, "name" | "label" | "page" | "utm"> = {},
) {
  if (typeof window === "undefined" || !analyticsAllowed()) return;

  const utm = captureUtm();
  const candidate: AnalyticsEvent = {
    name,
    label: clip(label, 120),
    page: window.location.pathname.slice(0, 200),
    ...(context.target ? { target: clip(context.target, 200) } : {}),
    ...(context.ctaType ? { ctaType: context.ctaType } : {}),
    ...(context.formId ? { formId: clip(context.formId, 80) } : {}),
    ...(context.formStatus ? { formStatus: context.formStatus } : {}),
    ...(context.detail ? { detail: clip(context.detail, 120) } : {}),
    ...(typeof context.clickY === "number" ? { clickY: context.clickY } : {}),
    ...(typeof context.scrollY === "number" ? { scrollY: context.scrollY } : {}),
    ...(context.sectionVersion ? { sectionVersion: clip(context.sectionVersion, 40) } : {}),
    ...(Object.keys(utm).length > 0 ? { utm } : {}),
  };

  // Contract check: a malformed payload is dropped instead of polluting reports.
  const parsed = analyticsEventSchema.safeParse(candidate);
  if (!parsed.success) {
    console.error(
      formatEventError(
        candidate,
        parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join(", "),
      ),
    );
    return;
  }
  const event = parsed.data as AnalyticsEvent;

  (window.__events ??= []).push(event);

  // Same formatter and field names the server uses, so client and server log
  // lines can be compared 1:1 while debugging.
  const position = deriveSectionPosition({
    ...(typeof event.clickY === "number" ? { clickY: event.clickY } : {}),
    ...(typeof event.scrollY === "number" ? { scrollY: event.scrollY } : {}),
  });
  if (!position.valid) {
    console.error(
      formatEventError(event, position.reason ?? "unknown", "implausible section position"),
    );
  }
  if (window.__eventsDebug) {
    console.log(formatEventLog(event, position.valid ? { documentY: position.documentY } : {}));
  }

  forwardToGa(event);

  const body = JSON.stringify({ events: [event] });


  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
    } else {
      void fetch(ENDPOINT, { method: "POST", body, keepalive: true });
    }
  } catch {
    // Analytics is best-effort — failures stay invisible to the visitor.
  }
}

/** CTA click, e.g. trackCtaClick("Chat per WhatsApp?", "primary_button", "/contact"). */
export function trackCtaClick(
  label: string,
  ctaType: CtaType,
  target?: string,
  detail?: string,
  clickY?: number,
) {
  trackEvent("cta_click", label, {
    ctaType,
    ...(target ? { target } : {}),
    ...(detail ? { detail } : {}),
    ...(typeof clickY === "number" ? { clickY } : {}),
  });
}

/**
 * CTA focus, e.g. trackCtaFocus("Booking section", "#book-meeting").
 * Fires when programmatic focus is moved to a tracked element after a
 * deliberate user action (e.g. a skip/jump link), not on every natural focus.
 * `scrollY` carries the effective scroll offset applied by the jump, so the
 * section actually shown can be derived from clickY + scrollY.
 */
export function trackCtaFocus(label: string, target?: string, detail?: string, scrollY?: number) {
  trackEvent("cta_focus", label, {
    ...(target ? { target } : {}),
    ...(detail ? { detail } : {}),
    ...(typeof scrollY === "number" ? { scrollY } : {}),
  });
}

/**
 * Fired after a deep-link scroll completed successfully: the target section
 * was found in the DOM, the computed scroll offset was applied and focus was
 * moved. Carries the effective `scrollY` and the target section's version so
 * reports can tie visibility to a specific deployment of the section.
 */
export function trackDeepLinkFocus(
  label: string,
  target: string,
  scrollY: number,
  sectionVersion: string,
  detail?: string,
) {
  trackEvent("deep_link_focus", label, {
    target,
    scrollY,
    sectionVersion,
    ...(detail ? { detail } : {}),
  });
}

/**
 * Fired when a deep-link target could not be found in the DOM after all
 * retries. Carries the target anchor, the number of retry attempts and a
 * machine-readable failure reason so broken deep links surface in reports.
 */
export function trackDeepLinkError(
  label: string,
  target: string,
  attempts: number,
  reason: string,
  detail?: string,
) {
  trackEvent("deep_link_error", label, {
    target,
    detail: clip(`attempts=${attempts} reason=${clip(reason, 60)}${detail ? ` ${detail}` : ""}`, 120),
  });
}

/**
 * Element became visible in the viewport (fires at most once per element and
 * page view — see `useElementView` in src/hooks/use-element-view.ts).
 */
export function trackElementView(label: string, detail?: string) {
  trackEvent("element_view", label, detail ? { detail } : {});
}

/** Form lifecycle event with a standardised status. */
export function trackForm(status: FormStatus, formId: string, label: string, detail?: string) {
  const name: EventName =
    status === "success"
      ? "form_success"
      : status === "error" || status === "invalid"
        ? "form_error"
        : status === "started"
          ? "form_start"
          : "form_submit";
  trackEvent(name, label, { formId, formStatus: status, ...(detail ? { detail } : {}) });
}

/**
 * Forwards the key conversions to GA4 (only active after analytics consent —
 * `gaEvent` is a no-op otherwise). See docs/analytics-events.md.
 */
export function gaEventFor(event: AnalyticsEvent): [string, Record<string, string>] | null {
  const target = (event.target ?? "").toLowerCase();
  const base = { event_label: event.label, page_path: event.page };
  if (event.name === "form_success") {
    return ["generate_lead", { ...base, form_id: event.formId ?? "" }];
  }
  if (event.name !== "cta_click") return null;
  if (target.includes("whatsapp") || target.includes("wa.me")) {
    return ["whatsapp_click", base];
  }
  if (event.ctaType === "booking_widget" || target.includes("book-meeting") || target.includes("bookings")) {
    return ["booking_click", base];
  }
  if (event.ctaType === "download") {
    return ["file_download", { ...base, link_url: event.target ?? "" }];
  }
  return null;
}

function forwardToGa(event: AnalyticsEvent) {
  const mapped = gaEventFor(event);
  if (mapped) gaEvent(mapped[0], mapped[1]);
}
